import React from 'react';
import {
  Box,
  BoxProps,
  Button,
  Divider,
  DividerProps,
  IconProps,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
  darken,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import {
  getSubjectColorNew,
  getUrlConvertedString,
  isMobileScreen,
} from '../../../../helper';
import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';

import {
  IGroupedSubTopic,
  IGroupedTopic,
} from '../../../../store/SubTopicSlice';
import { getProgressState } from '../../../../store/ProgressSlice';
import {
  checkQuizzes,
  checkResources,
  checkTopicPlanner,
  checkVideos,
} from '../../Subject/components/SubTopicList';

const subTopicListStackProps: StackProps = {
  borderRadius: '20px',
  padding: isMobileScreen() ? '10px' : '20px 30px',
  sx: { backgroundColor: TP_COLORS.white },
  margin: '30px 0px',
};

const dividerProps: DividerProps = {
  flexItem: true,
  orientation: 'vertical',
  variant: 'fullWidth',
  sx: { margin: '3px 10px' },
};

const subTopicItemBoxProps: BoxProps['sx'] = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px',
  sx: {
    borderBottom: '1px solid #CBD5E1',
    '&:last-child': {
      borderBottom: 0,
    },
  },
};

const checkIconProps: IconProps['sx'] = {
  fontSize: 'medium',
  style: {
    height: '30px',
    width: '30px',
    marginRight: '8px',
  },
};

const textButtonProps: TypographyProps = {
  variant: 'subtitle2',
  color: '#647488',
};

interface SubTopicListProps {
  selectedTopic: IGroupedTopic;
}

const SubTopicList: React.FC<SubTopicListProps> = ({ selectedTopic }) => {
  const navigate = useNavigate();
  const isMobile = isMobileScreen();

  // Store
  const progressState = useAppSelector(getProgressState);

  const { subjectProgress } = progressState;

  const subjectColor = getSubjectColorNew(selectedTopic.subject);

  const handleButtonClick = (subTopicData: IGroupedSubTopic) => {
    const urlConvertedCurriculum = getUrlConvertedString(
      selectedTopic.curriculum
    );

    const urlConvertedSubject = getUrlConvertedString(selectedTopic.subject);

    const urlConvertedTopic = getUrlConvertedString(selectedTopic.name);

    const urlConvertedSubTopic = getUrlConvertedString(
      subTopicData?.name || ''
    );

    navigate(
      `${
        process.env.PUBLIC_URL
      }/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${
        subTopicData._id
      }${subTopicData.resources?.length ? '' : '/quiz'}`
    );
  };

  const getSubTopicProgress = (topicId: string, subTopicId: string) => {
    let isCompleted = false;

    if (subjectProgress) {
      const topicProgress = subjectProgress.topics.find(
        (item) => item._id === topicId
      );

      if (topicProgress && topicProgress.subTopics.length) {
        const subTopicProgress = topicProgress.subTopics.find(
          (item) => item._id === subTopicId
        );

        if (subTopicProgress && subTopicProgress.completion === 100) {
          isCompleted = true;
        }
      }
    }
    return isCompleted;
  };

  const getButtonText = (subTopic: IGroupedSubTopic) => {
    if (selectedTopic) {
      const subTopicProgress = getSubTopicProgress(
        selectedTopic._id,
        subTopic._id
      );
      if (subTopicProgress) {
        return 'Explore Again';
      }
    }

    if (
      subTopic.name === 'Topic Planner' ||
      (selectedTopic && selectedTopic.name === 'Overview')
    ) {
      return 'Overview';
    }

    if (checkDisabled(subTopic)) {
      return 'Upcoming...';
    }

    return 'Start Learning';
  };

  const checkDisabled = (subTopic: IGroupedSubTopic) => {
    if (
      !checkResources(subTopic) &&
      !checkVideos(subTopic) &&
      !checkQuizzes(subTopic)
    ) {
      return true;
    }

    return false;
  };

  return (
    <Stack {...subTopicListStackProps}>
      {selectedTopic.subTopics.map((subTopicItem, subTopicIndex) => (
        <Box key={subTopicIndex} {...subTopicItemBoxProps}>
          <Box display="flex" alignItems="center">
            {getSubTopicProgress(selectedTopic._id, subTopicItem._id) ? (
              <CheckCircleIcon
                sx={{ color: subjectColor }}
                {...checkIconProps}
              />
            ) : (
              <RadioButtonUncheckedIcon
                sx={{ color: subjectColor }}
                {...checkIconProps}
              />
            )}
            <Stack
              padding="8px"
              sx={{
                marginLeft: {
                  sm: '30px',
                  md: '40px',
                },
              }}
            >
              <Typography
                variant="subtitle1"
                color={TP_COLORS.gray.darken}
                sx={{ fontSize: { xs: '16px', sm: '18px' } }}
              >
                {subTopicItem.name}
              </Typography>
              <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                {checkResources(subTopicItem) && (
                  <Typography {...textButtonProps}>Resources</Typography>
                )}

                {checkTopicPlanner(subTopicItem.name) &&
                  checkResources(subTopicItem) && <Divider {...dividerProps} />}

                {checkTopicPlanner(subTopicItem.name) && (
                  <>
                    {checkVideos(subTopicItem) && (
                      <>
                        <Typography {...textButtonProps}>Videos</Typography>
                        <Divider {...dividerProps} />
                      </>
                    )}

                    <Typography {...textButtonProps}>Quizzes</Typography>
                  </>
                )}
              </Box>
            </Stack>
          </Box>

          <Button
            size={isMobile ? 'small' : 'medium'}
            color="secondary"
            variant="contained"
            onClick={() => handleButtonClick(subTopicItem)}
            disabled={checkDisabled(subTopicItem)}
            sx={{
              minWidth: 'fit-content',
              width: isMobile ? '110px' : '135px',
              backgroundColor: subjectColor,
              '&:hover': {
                backgroundColor: darken(subjectColor, 0.3),
              },
            }}
          >
            {getButtonText(subTopicItem)}
          </Button>
        </Box>
      ))}
    </Stack>
  );
};

export default SubTopicList;
